import { useIdeState } from '@common/ide/state';
import { useSelector } from '@common/performance/useSelector';
import { Entity } from '@common/types/Entity';
import { getIDEUrl } from '@common/url';
import { LoadingDIcon } from '@prophecy/icons';
import { theme, BaseColor } from '@prophecy/theme';
import {
  Button,
  ButtonGroup,
  Dialog,
  Ellipsis,
  overlayStyle,
  RadialIconContainer,
  Stack,
  StackItem,
  Tooltip
} from '@prophecy/ui-v3';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { toFormattedTime } from '@prophecy/utils/date';
import { capitalize } from 'lodash-es';
import { useEffect } from 'react';
import styled from 'styled-components';
import { format as timeAgoFormat } from 'timeago.js';

import { useGlobalVariables } from '../../../context/globalConfig/securedConfigHook';
import { useGraphQlMutation } from '../../../data/util';
import { LAST_OBSERVATION_IDE_URL } from '../../../utils/localstorage-keys';
import { useObservationIDE, useProjectObservationsData } from '../hooks';
import {
  DataQualityFeedbackQuery,
  DataQualityFeedbackQueryResponse,
  DataQualityFeedbackQueryVariable,
  ObservationType
} from '../query';
import { DQObservationMetadata, ObservationMetadata, ObservationState, ResetObservationKeyWord } from '../redux/types';
import { ProjectEventTypes, FeedbackTypes, ProjectStatusTypes } from '../types';
import {
  getFilteredProjectEventsList,
  getIconDetailFromStatus,
  getJobTemplate,
  getLabelValue,
  getParsedTemplate,
  getProgressString,
  isJobRunObservation
} from '../util';
import { ReadOnlyBanner } from './ReadonlyBanner';
import { RunDetail } from './RunDetail';
import { StyledCard, StyledList, StyledStack, StyledStackItem, StyledTags } from './styled';

const StyledDetailContent = styled.div`
  line-height: ${theme.spaces.x32};
`;

const ProjectMonitoringDetail = ({ observation, fabricId }: { fabricId: string; observation: ObservationType }) => {
  const status = (observation?.status || ProjectStatusTypes.INITIALIZED).toUpperCase();
  const isExpectation = !isJobRunObservation(observation);
  const { icon, color, currentStatus } = getIconDetailFromStatus(status);
  //TODO enable it in next sprint once backend is done
  const isFailed = false; //status !== StatusTypes.SUCCEEDED && status !== StatusTypes.RUNNING && status !== StatusTypes.INITIALIZED;
  const startTime = timeAgoFormat(observation.startDate);
  let progress = getProgressString(observation);

  //TODO backend need to provide date as timestamp only for airflow fabric as well
  let totalTime = observation.endDate ? Number(observation.endDate) - Number(observation.startDate) : undefined;
  const { mutateAsync, isLoading } = useGraphQlMutation<
    DataQualityFeedbackQueryResponse,
    DataQualityFeedbackQueryVariable
  >(DataQualityFeedbackQuery);

  const handleFeedbackSubmit = (feedback: FeedbackTypes) => {
    mutateAsync(
      { runType: Entity.Expectation.toLowerCase(), runId: observation.prophecyJobId, feedback },
      {
        onSuccess: (_resp) => {
          toast.success({ content: 'Feedback updated successfully.' });
        }
      }
    );
  };

  const openDetail = () => {
    Dialog.info({
      'data-test-id': 'observation-detail-info-dialog',
      title: `Observation Detail`,
      size: 'l',
      children: (
        <Stack width='100%' height='100%' gap={theme.spaces.x10} padding={`${theme.spaces.x20} 0`}>
          <StyledDetailContent>{getParsedTemplate(observation.summary, observation)}</StyledDetailContent>
        </Stack>
      ),
      okButton: { children: 'OK', block: false }
    });
  };

  return (
    <StyledCard hasHoverEffect={false}>
      <Stack direction='horizontal' gap={theme.spaces.x20} width='100%' alignY='start' align='space-between'>
        <Tooltip title={`Status: ${capitalize(status)}`}>
          <Stack width='60px'>
            <RadialIconContainer tone={BaseColor.primary ?? color} gradient={true}>
              {icon}
            </RadialIconContainer>
          </Stack>
        </Tooltip>
        <StackItem grow='1'>
          <Stack gap={theme.spaces.x20} width='100%'>
            <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center' align='space-between'>
              <StackItem grow='1'>
                <Stack direction='horizontal' align='space-between' gap={theme.spaces.x4} alignY='center'>
                  {isExpectation ? (
                    <Ellipsis tooltipProps={{ overlayStyle }} tooltip={true}>
                      {getParsedTemplate(observation.summary, observation)}
                    </Ellipsis>
                  ) : (
                    getJobTemplate(observation, currentStatus as ProjectStatusTypes)
                  )}
                </Stack>
              </StackItem>
              {observation.detail && (
                <Button
                  size='s'
                  onClick={openDetail}
                  variant='secondaryGray'
                  data-test-id='project-observation-more-button'>
                  More
                </Button>
              )}
            </Stack>
            <StackItem grow='1'>
              <Ellipsis tooltipProps={{ overlayStyle }} tooltip={true}>
                {isExpectation ? (
                  <Stack width='100%' direction='horizontal' gap={theme.spaces.x16} alignY='center'>
                    {getLabelValue('Created', startTime)}
                    {getLabelValue('Expectation', observation?.prophecyJobName)}
                    {getLabelValue('Dataset', observation?.tableName)}
                    {getLabelValue('Column', observation?.columnName)}
                    {observation?.projectName &&
                      getLabelValue('Project', observation?.projectName, observation.projectId)}
                  </Stack>
                ) : (
                  <Stack
                    width='100%'
                    align='space-between'
                    direction='horizontal'
                    gap={theme.spaces.x12}
                    alignY='center'>
                    <Stack direction='horizontal' gap={theme.spaces.x16} alignY='center'>
                      {getLabelValue('Created', startTime)}
                      {observation.endDate !== undefined &&
                        getLabelValue('Finished', timeAgoFormat(observation.endDate))}
                      {totalTime !== undefined &&
                        getLabelValue('Total Time', isNaN(totalTime) ? '0' : toFormattedTime(totalTime))}
                      {progress !== undefined && getLabelValue('Progress', progress)}
                    </Stack>
                    {fabricId && <RunDetail fabricId={fabricId} {...observation} />}
                  </Stack>
                )}
              </Ellipsis>
            </StackItem>
            {isFailed && (
              <ButtonGroup size='s' disabled={isLoading}>
                <ButtonGroup.Item
                  onClick={() => handleFeedbackSubmit(FeedbackTypes.Fixed)}
                  data-test-id='project-observations-feedback-fixed-button'>
                  {FeedbackTypes.Fixed}
                </ButtonGroup.Item>
                <ButtonGroup.Item
                  onClick={() => handleFeedbackSubmit(FeedbackTypes.Investigating)}
                  data-test-id='project-observations-feedback-investigating-button'>
                  {FeedbackTypes.Investigating}
                </ButtonGroup.Item>
                <ButtonGroup.Item
                  onClick={() => handleFeedbackSubmit(FeedbackTypes.Expected)}
                  data-test-id='project-observations-feedback-expected-button'>
                  {FeedbackTypes.Expected}
                </ButtonGroup.Item>
              </ButtonGroup>
            )}
          </Stack>
        </StackItem>
      </Stack>
    </StyledCard>
  );
};

export function ProjectObservations() {
  const projectId = useIdeState((state) => state.project.id);
  const observationSelectedFileMap = useIdeState((state) => state.ide.monitoringSelectedFiles)[Entity.Observation];
  const activeEventEntity = observationSelectedFileMap?.selectedEntity;
  const isDQExpectationEnabled = useGlobalVariables().isDataQualityEnabled;
  const { changeProjectEvent } = useObservationIDE();
  useEffect(() => {
    if (projectId) {
      localStorage.setItem(LAST_OBSERVATION_IDE_URL, getIDEUrl({ uid: projectId, entity: Entity.Observation }));
    }
  }, [projectId]);

  const { loadMore, handleDateChange, hasNextPage, execMonitoringData, isLoading, handleStatusChange } =
    useProjectObservationsData();
  const dq = useSelector<ObservationState, DQObservationMetadata>((state) => state.dataQuality);
  const meta = useSelector<ObservationState, ObservationMetadata>((state) => state.observationMetadata);
  const observations = dq.observations || [];
  const eventType = dq.eventType;
  const allObservations = [...observations, ...execMonitoringData.runData];

  const { eventCountMap, currentObservations } = getFilteredProjectEventsList(
    allObservations,
    eventType,
    dq.selectedStatus
  );
  const isDataLoading = meta.isLoadingMoreData || isLoading || execMonitoringData.isLoadingRunData;
  const getFilterButtonUI = (event: ProjectEventTypes) => {
    const isSelected = event === eventType;
    return (
      <StyledTags
        onClick={() => changeProjectEvent(event)}
        icon={isDataLoading ? <LoadingDIcon spin={true} type='extended' /> : null}
        tone={isSelected ? BaseColor.primary : BaseColor.gray}
        countCircle={true}
        size='l'
        count={eventCountMap[event]}
        data-test-id='project-observation-filter-button'>
        {event}
      </StyledTags>
    );
  };

  const isLoadMore = hasNextPage || execMonitoringData.hasNextRunPage;
  const observationCount = currentObservations.length;
  const dataSource = currentObservations.map((rowData, i) => ({ rowData, key: i }));
  //Refresh table once data filtered on basis of event type or observation Date or active entity id otherwise empty table will be shown
  const tableKey = eventType + meta.startDateFrom + activeEventEntity + dq.selectedStatus;
  return (
    <>
      <ReadOnlyBanner
        startDateFrom={meta.startDateFrom === ResetObservationKeyWord ? undefined : (meta.startDateFrom as number)}
        startDateTo={meta.startDateTo}
        status={dq.selectedStatus}
        handleStatusChange={handleStatusChange}
        handleDateChange={handleDateChange}
        loading={isDataLoading}
      />
      <StyledStack alignY='center' gap={theme.spaces.x8} align={'space-between'} width='100%' height='100%'>
        <Stack alignY='center' direction='horizontal' gap={theme.spaces.x6}>
          {getFilterButtonUI(ProjectEventTypes.AttentionRequired)}
          {getFilterButtonUI(ProjectEventTypes.All)}
          {getFilterButtonUI(ProjectEventTypes.JobRuns)}
          {isDQExpectationEnabled && getFilterButtonUI(ProjectEventTypes.QualityIncidents)}
        </Stack>
        <StyledStackItem grow='1'>
          <Stack
            alignY='top'
            width='100%'
            padding={`${theme.spaces.x10} 0 ${theme.spaces.x48}`}
            gap={theme.spaces.x20}
            height='100%'>
            <StyledList
              key={tableKey}
              virtualize={true}
              rowHeight={118}
              footer={() =>
                isLoadMore &&
                observationCount > 0 && (
                  <Stack width='100%' alignY='center'>
                    <Button
                      loading={isDataLoading}
                      onClick={() => loadMore()}
                      size='s'
                      variant='secondaryGray'
                      data-test-id='project-observation-load-more-button'>
                      Load More
                    </Button>
                  </Stack>
                )
              }
              loading={isDataLoading}
              emptyText={observationCount < 1 && !isDataLoading ? 'No observation data.' : ''}
              data={dataSource}
              renderItem={(record, index) => (
                <ProjectMonitoringDetail
                  observation={record.rowData as ObservationType}
                  fabricId={meta.fabricId}
                  key={index}
                />
              )}
              data-test-id='project-observations-list'
            />
          </Stack>
        </StyledStackItem>
      </StyledStack>
    </>
  );
}
